import _ from 'lodash'
import * as blueprint from '@adc/blueprint'
import { Memoize } from 'lodash-decorators'
import moment from 'moment'
import canUseDom from '../../util/canUseDom'
import {
  SOLD,
  PENDING,
  FOR_SALE,
  UNDER_CONTRACT,
  NEW_DATE_PENDING,
  ACTIVE_POSTPONED_TO,
  HIDE_STARTING_BID_STATES,
  CLOSING_WAITING_PERIOD_DAYS,
  PREAUCTION_URGENCY_PERIOD_THRESHOLD,
  POLLING_INTERVAL_NOT_IN_AUCTION,
  POLLING_TIMER_FINISHING_THRESHOLD,
  POLLING_INTERVAL_FINAL_OFFER_PENDING,
  POLLING_TIMER_AFTER_AUCTION_THRESHOLD,
  POLLING_INTERVAL_IN_AUCTION_LOGGED_IN,
  POLLING_INTERVAL_PREPARING_CONTRACT_FORM,
  POLLING_INTERVAL_IN_AUCTION_NOT_LOGGED_IN,
} from '../constants'
import * as biddingStatusConstants from '../constants/biddingStatus'
import { url, nextBusinessDay } from '../utils/biddingStatus'
import { isFinalOfferPending } from '../utils/finalOffer'
/**
 * NOTE: This class is immutable, meaning that once it is instantiated, there are no setters --
 * attributes can only be read, and will be memoized in the future.
 *
 * TODO: Make all identifiers a single case to avoid caps errors. E.g. 'Sold Subject To` should be
 * referenced internally as 'sold subject to`, or we can even convert it to lower snake case to make
 * it easer to work with.
 */
export default class BiddingStatus {
  constructor(bidding, user = {}, property = {}) {
    this._status = bidding.status
    this._preAuction = bidding.preAuction
    this._user = user
    this._property = property
    this._property_details = this.isREO
      ? property.property_details
      : property.property_detail
  }

  static constants = biddingStatusConstants

  /**
   * PUBLIC PROPERTIES
   */

  get isREO() {
    return !_.get(this._property, 'is_trustee', false)
  }

  get auctionNumber() {
    return this._status.auction_number
  }

  get auctionId() {
    return this._status.auction_id
  }

  get auctionStatus() {
    return this._status.auction_status || 'Not started'
  }

  get isSoldSubjectTo() {
    return this.wasManuallyClerked
      ? Boolean(this._status.bid_pending_confirmation)
      : this._status.auction_status === 'Sold subject to'
  }

  get propertyState() {
    return this.isREO
      ? _.get(this._property_details, 'address.state')
      : _.get(this._property_details, 'property_state')
  }

  /**
   * This id is global throughout the whole system
   */
  get globalPropertyId() {
    return this._status.property_id
  }

  /**
   * This id is unique for this property within the current event
   */
  get eventPropertyId() {
    return this._property_details.property_id
  }

  get redcid() {
    return this.isREO
      ? _.get(this._property, 'transaction_details.event_item', '')
      : _.get(this._property, 'product_details.event_item', '')
  }

  get propertyAddress() {
    return this._property_details.address.complete_address
  }

  /**
   * IMPORTANT: this property considers the user registered even if they
   * have not confirmed yet.  Non-confirmation will be addressed separately,
   * for example, by showing the options (like Bid CTA), but disabling it
   * when user is not yet confirmed.
   * @return {Boolean} [description]
   */
  get isUserRegisteredForAuction() {
    return this._user.logged_in && this._status.registration_status === 1
  }

  get isRepeatBuyer() {
    return (
      this._user.is_bid_deposit_override_active &&
      moment(this._user.bid_deposit_expiration_date).diff(
        this.currentServerTime
      ) > 0
    )
  }

  // TODO: Move to user model
  get userId() {
    return this._user.user_id
  }

  get isUserNotConfirmed() {
    return this._user.logged_in && !this._user.account_confirm
  }

  get doesUserHaveConfirmedToken() {
    return _.get(this._user, 'tokenValues.account_confirm', false)
  }

  get isBidDepositOverride() {
    return this._user.logged_in
      ? this.isRepeatBuyer || this._user.institution_deposit_ord
      : false
  }

  get isAuctionInProgress() {
    return this.auctionStatus === 'Live'
  }

  get isAuctionStatusUnknown() {
    return this.auctionStatus === 'Unknown'
  }

  /**
   * TODO: Even though we have this setting, the old pdp does not respect the value. So, for
   * example, when this value is false, the old pdp still requires a confirmation before placing
   * a bid.
   *
   * @returns {Boolean} whether the auction event requires confirmation before a bid can be placed
   */
  get requiresConfirmationForBid() {
    return this._property.transaction_details.is_bid_pending_confirmation
  }

  get auctionSequence() {
    return this._property_details.auction_sequence
  }

  get hasHighestUserBid() {
    return this._status.has_highest_user_bid
  }

  get submitOfferDeadline() {
    return this.biddingEndTime ? this.biddingEndTime.add(1, 'day') : null
  }

  /**
   * An auction being finished does not mean that is has been sold. Finished means it's done,
   * either through normal a auction ending or through a termination.
   */
  get isAuctionFinished() {
    return this.isAuctionGoneOrSold || this.isAuctionTerminated
  }

  get isAuctionGoneOrSold() {
    return this.isAuctionGone || this.isAuctionSold
  }

  get isAuctionGone() {
    return this.auctionStatus === 'Gone'
  }

  get isAuctionSold() {
    if (this.isTPSLive || this.isFCLMarketing) {
      return ['6', '18'].includes(this.auctionDayStatus)
    }
    return (
      ['Sold', 'Sold subject to', 'Sold to Lender'].includes(
        this.auctionStatus
      ) || [SOLD, PENDING, UNDER_CONTRACT].includes(this.propertyStatusLabel)
    )
  }

  get isAuctionSoldToLender() {
    return this.auctionStatus === 'Sold to Lender'
  }

  get isAuctionCancelled() {
    return this.auctionStatus === 'Cancelled'
  }

  get isAuctionTerminated() {
    return ['Postponed', 'Cancelled'].includes(this.auctionStatus)
  }

  get isAuctionPending() {
    return this.auctionStatus === 'Not started'
  }

  get isReAuctionPending() {
    return this.didAuctionFallOut || this.didNotSell
  }

  get isAuctionNewDatePending() {
    return this.propertyStatusLabel === NEW_DATE_PENDING
  }

  get didAuctionFallOut() {
    return this.isTPSOhio && this.isAuctionStatusUnknown
  }

  get didNotSell() {
    return (
      this.isTPSOhio &&
      this.isAuctionGone &&
      (!this.hasBids || this.reserveStatus === 'RESERVE_NOT_MET')
    )
  }

  /**
   * Determines if an auction is Live or Online
   */
  get isOnlineEvent() {
    return this.isREO
      ? this._property.transaction_details.online_event
      : this._property.auction.auction_details.online_event
  }

  get isLiveEvent() {
    return !this.isOnlineEvent
  }

  /** this is gross - ideally we use status_label but not fully reliable
  currently.  Should probably revisit this... **/
  get isAuctionComingSoon() {
    return (
      this._property &&
      this._property.transaction_details &&
      this._property.transaction_details.is_coming_soon
    )
  }

  get isAuctionActive() {
    return !this.isAuctionSold && !this.isAuctionComingSoon
  }

  get isActiveWithNoBid() {
    return this.isAuctionInProgress && !this.hasBids
  }

  get hasBids() {
    return Number(this._status.bid_count) > 0
  }

  get isStatusMissing() {
    return _.isEmpty(this._status)
  }

  get currentServerTime() {
    return this._status.database_server_time
      ? moment(this._status.database_server_time)
      : null
  }

  get startDateTime() {
    return this._status.start_date_time
  }

  get endDateTime() {
    return this._status.end_date_time
  }

  get biddingStartTime() {
    return this._status.start_date_time
      ? moment(this._status.start_date_time)
      : null
  }

  get biddingEndTime() {
    return this._status.end_date_time
      ? moment(this._status.end_date_time)
      : null
  }

  get provideByTime() {
    return this.biddingEndTime
      ? nextBusinessDay(
          moment(this.biddingEndTime).add(CLOSING_WAITING_PERIOD_DAYS, 'days')
        )
      : null
  }

  get isPostponed() {
    return (
      this._property.status?.toLowerCase().includes('postponed') ||
      this.auctionStatus === 'Postponed'
    )
  }

  get postponedDate() {
    return this._property_details.postponement_date
  }

  get postponedTime() {
    return this._property_details.postponement_time
  }

  get postponedTimeWithTimezone() {
    return this._property_details.postponement_time + '-0700'
  }

  get auctionDayStatus() {
    return this._property.auction.auction_day_status
  }

  get provisionalSaleDate() {
    return this._property_details.provisional_sale_date
  }

  get provisionalSaleTime() {
    return (
      moment(this.provisionalSaleDate).format('MMM DD') +
      ', ' +
      moment(this.startDateTime)
        .tz(blueprint.utils.calculateTimeZone())
        .format('hh:mm a z')
    )
  }

  get isAuctionPendingSaleConfirmation() {
    return (
      !this.isREO &&
      this.isAuctionSold &&
      ['4', '7', '14'].includes(this.auctionDayStatus)
    )
  }

  isPastDue() {
    return moment().isAfter(this.provideByTime)
  }

  isTimeExtended(newTime) {
    return (
      newTime > this.remainingSeconds &&
      this.isAuctionInProgress &&
      !this.requiresConfirmationForBid
    )
  }

  wasTimeExtended(oldTime) {
    return (
      this.remainingSeconds > oldTime &&
      this.isAuctionInProgress &&
      !this.requiresConfirmationForBid
    )
  }

  get preAuctionStartTime() {
    return this._preAuction.start_date_time
  }

  get preAuctionEndTime() {
    return this._preAuction.end_date_time
  }

  get preAuctionDateRange() {
    return [this.preAuctionStartTime, this.preAuctionEndTime]
  }

  get auctionDate() {
    return this.isREO
      ? this._property.transaction_details.auction_date
      : this._property.auction.auction_date
  }

  get auctionTime() {
    return this.isREO
      ? _.get(this._property, 'transaction_details.auction_time', '')
      : _.get(this._property, 'event_details.sale_time', '')
  }

  /**
   * If the data has not been scheduled yet then these two fields will be empty strings
   */
  get hasAuctionDateSet() {
    return !!this.auctionDate && !!this.auctionTime
  }

  /**
   * NOTE: Not altogether sure what that is_presale_valid thing is from platform, so I added an
   * additional date range check here to make sure we follow what is in the ticket AC
   */
  get isAuctionInPreSale() {
    // NOTE: The cryptic last 2 params ensure we get inclusive matching
    const currentlyInDateRange = moment(Date.now()).isBetween(
      this._preAuction.start_date_time,
      this._preAuction.end_date_time,
      null,
      '[]'
    )

    return (
      !!this._preAuction.is_presale_event &&
      currentlyInDateRange &&
      this._preAuction.is_presale_valid
    )
  }

  get isInMaoUrgentPeriod() {
    const urgentStart = moment(this.preAuctionEndTime).subtract(
      PREAUCTION_URGENCY_PERIOD_THRESHOLD,
      'days'
    )

    return moment(Date.now()).isSameOrAfter(urgentStart, 'day')
  }

  get isProxyBiddingAllowed() {
    return this._property.transaction_details.is_proxy
  }

  get isProxyBiddingActive() {
    return (
      this.isProxyBiddingAllowed &&
      this.isAuctionPending &&
      !this.isAuctionInPreSale
    )
  }

  get highestProxyBidByUser() {
    return this._status.proxy_bid_amount || null
  }

  get hasUserPlacedProxyBid() {
    return Boolean(this.highestProxyBidByUser)
  }

  get nextProxyBidAmount() {
    if (this.isProxyBiddingActive) {
      return Math.max(
        this.startBidAmount,
        this.hasUserPlacedProxyBid && this.highestProxyBidByUser + 1
      )
    }
    return null
  }

  get bidIncrement() {
    return this._status.bid_increment
  }

  get currentBidAmount() {
    return this._status.current_bid_amount || 0
  }

  get currentUserBidAmount() {
    return this._status.current_user_bid_amount || 0
  }

  get startBidAmount() {
    return this._status.start_bid_amount
  }

  get nextBidAmount() {
    const amount = this.hasBids
      ? this.currentBidAmount + this.bidIncrement
      : this.startBidAmount

    return Math.ceil(amount)
  }

  get winningBidAmount() {
    return this._status.winning_bid_amount || null
  }

  get isBidAgainEnabled() {
    return this._status.bid_again
  }

  // The number of times an MLH user has clicked "Bid Again" for this property since the last bid
  get bidAgainCount() {
    return this._status.bid_again_count
  }

  get hasPreviousValuation() {
    return !!this._property.transaction_details.bid_info.previous_value
  }

  get shouldShowSellerDisclosure() {
    return (
      this._status.show_seller_disclosure &&
      !!this._status.seller_disclosure_label.length
    )
  }

  get shouldShowPostBidBox() {
    /**
     * We take advantage of API cache for status_label which will show For Sale once auction sold
     * i.e. during the cache duration, we will still show regular bidbox (with winning status handled) instead of post bidbox
     */
    return this.isAuctionSold && this.propertyStatusLabel !== FOR_SALE
  }

  get sellerDisclosureInformationTrigger() {
    return this._status.seller_disclosure_label
  }

  get sellerDisclosureInformation() {
    return this._status.seller_disclosure_tooltip_text
  }

  /**
   * The next two methods are closely related. This first one controls whether we should show the
   * text that relates to the status of the reserve currently posted -- you can find that value by
   * calling *reserveStatus*.
   */
  get shouldShowAuctionReserveText() {
    return (
      this.auctionStatus !== 'Not started' &&
      (!this.isAuctionInPreSale || !this.isProxyBiddingAllowed) &&
      !this.isAuctionGoneOrSold &&
      !this.isAuctionComingSoon &&
      this.isOnlineEvent
    )
  }

  /**
   * This flag controls whether the amount of the reserve should also be shown to the user, not just
   * how the current bids relate to the reserve (under reserve, met reserve etc)
   */
  get shouldShowAuctionReserveAmount() {
    return (
      !!this._property.transaction_details.is_reserve_displayed &&
      !!this.bidValuationInformation.reserve_amount &&
      !this.isAuctionComingSoon && // This is legacy logic that should be re-evaluated eventually, stops reserve from showing on coming soon and won assets
      !this.hasUserWonAuction
    )
  }

  get shouldShowEstCreditBidAmount() {
    return !isNaN(this.estCreditBid)
  }

  /**
   * This flag controls weather or not to show the notify me CTA if the reserve is not disclosed. If this returns false, the reserve shows not disclosed.
   */
  get shouldShowAuctionReserveNotifyMe() {
    return (
      !this.shouldShowAuctionReserveAmount &&
      !this.isAuctionInProgress &&
      !this.hasUserWonAuction &&
      (!this.isAuctionGoneOrSold || this.isAuctionNewDatePending)
    )
  }

  get shouldShowAuctionEstCreditBidNotifyMe() {
    return !this.shouldShowEstCreditBidAmount && this.isEstCreditBidPending
  }

  get showOpeningBid() {
    if (this.isREO) {
      return (
        !HIDE_STARTING_BID_STATES.includes(this.propertyState) &&
        !this.isAuctionSold &&
        (this.isLiveEvent ||
          this.isAuctionPending ||
          this.isActiveWithNoBid ||
          this.isAuctionComingSoon)
      )
    } else {
      return !!this.openingBid
    }
  }

  get shouldShowEstCreditBid() {
    return (
      !!this._status.show_reserve_met &&
      !!_.get(this._property, 'price_area.max_bid', null)
    )
  }

  get openingBid() {
    if (this.isREO) {
      return this.bidValuationInformation.start_bid_amount
    } else if (this.isTPSOnline) {
      return this.startBidAmount
    } else {
      return _.get(this._property, 'price_area.opening_bid', null)
    }
  }

  get estCreditBid() {
    return _.get(this._property, 'price_area.max_bid')
  }

  get isEstCreditBidPending() {
    return _.toLower(this.estCreditBid) === 'coming soon'
  }

  get reserveStatus() {
    return this._status.reserve_met_text_id
  }

  /**
   * From the old platform code, *reserve_met_text_id* could be either:
   * - "RESERVE_MET"
   * - "RESERVE_NOT_MET"
   * - "RESERVE_NOT_SET"
   */
  get hasReserveBeenMet() {
    return this.isAuctionFinished
      ? Boolean(this._status.winning_bid_meets_reserve)
      : this.reserveStatus === 'RESERVE_MET'
  }

  get reserveAmount() {
    return this._status.auction_seller_reserve
  }

  get isUserHighestBidder() {
    return !!this._status.is_highest_bidder
  }

  get hasUserBeenOutbid() {
    return this._status.out_bid
  }

  /**
   * Perhaps this names make more sense as hasAdcCounterBid, or something like that. Might revisit
   * this.
   */
  get shouldUserBidAgain() {
    return this.isBidAgainEnabled && !this.hasReserveBeenMet
  }

  get isSellerCounterBid() {
    // seller_disclosure_label can be 'Kentucky - Counter Bid', and not just 'Counter Bid'
    return this._status.seller_disclosure_label.includes('Counter Bid')
  }

  /**
   * NOTE: We do not take into account whether the user has reached the reserve amount or not here.
   * This matches what we have in the legacy app.
   */
  get hasUserWonAuction() {
    // The BiddingStatus API exposes winning bid amount only to the winning bidder (BID-276)
    return Boolean(this.winningBidAmount)
  }

  get wasManuallyClerked() {
    return this._status.clerked_from === 'Normal'
  }

  get hasUserLostAuction() {
    return this.hasUserBeenOutbid && this.isAuctionSold
  }

  /**
   * This means that a user has placed a bid on a property, but:
   * 1) The bid they added does not exceed the reserve, even if it is the current highest
   * 2) They have been outbid, which means they have bidded at least once
   */
  get requireAnotherUserBid() {
    return (
      !this.hasReserveBeenMet &&
      (this.isUserHighestBidder || this.hasUserBeenOutbid)
    )
  }

  /**
   * This returns all the attributes from the bidding status that is actually displayed
   * to an end user in a component. This way, we can confidently know the complete subset of
   * bidding information we need for display purposes when showing auction valuation information.
   *
   * @return {Object}
   */
  @Memoize
  get bidValuationInformation() {
    return _(this._status)
      .pick(['start_bid_amount', 'current_bid_amount', 'bid_increment'])
      .tap(obj => {
        /**
         * NOTE: Assets with auctions that are coming soon will not have a bidding status
         * so this will fail right now. When we deal with more stories later on, we should never hit
         * this part of the code.
         *
         * --- Actually, it appears "coming soon" does have bidding status but no start/end datetimes,
         * so for now adding some checks here to prevent .format from blowing up.
         */
        obj.formatted_start_date = this.biddingStartTime
          ? this.biddingStartTime.format('MM/DD/YYYY')
          : ''
        obj.formatted_end_date = this.biddingEndTime
          ? this.biddingEndTime.format('MM/DD/YYYY')
          : ''
        obj.bid_deposit = this._property.transaction_details.deposit
        obj.previous_value = _.get(
          this._property,
          'transaction_details.bid_info.previous_value',
          ''
        )
        obj.reserve_amount = this._status.auction_seller_reserve
        obj.bid_deposit_override = this.isBidDepositOverride
      })
      .value()
  }

  get buyersPremiumFlatAmount() {
    return this.isTPSOnline
      ? this._property_details.buyers_premium_amt
      : this._property.transaction_details.buyers_premium_amt
  }

  get buyersPremiumMinAmount() {
    const amount = this.isTPSOnline
      ? this._property_details.buyers_premium_min_amt
      : this._property.transaction_details.buyers_premium_min_amt
    // Default buyers_premium_min_amt to 0.
    return amount === undefined ? 0 : amount
  }

  get buyersPremiumPercentage() {
    return this.isTPSOnline
      ? this._property_details.buyers_premium_pct
      : this._property.transaction_details.buyers_premium_pct
  }

  get buyersPremiumType() {
    return this.isTPSOnline
      ? this._property_details.buyers_premium_type
      : this._property.transaction_details.buyers_premium_type
  }

  get earnestMoneyDepositFlatAmount() {
    return this._property.transaction_details.earnest_money_deposit_amt
  }

  get earnestMoneyDepositMinAmount() {
    return this._property.transaction_details.earnest_money_deposit_min_amt
  }

  get earnestMoneyDepositPercentage() {
    return this._property.transaction_details.earnest_money_deposit_pct
  }

  get earnestMoneyDepositType() {
    return this._property.transaction_details.earnest_money_deposit_type
  }

  /**
   * Returns remaining seconds until:
   * (a) bidding starts (if bidding not yet started)
   * (b) bidding ends (if bidding already started)
   *
   * @return {number} Seconds remaining
   */
  get remainingSeconds() {
    if (this.canCountDown) {
      const targetTime = this.isAuctionInProgress
        ? this.biddingEndTime
        : this.biddingStartTime
      return Math.max(targetTime.diff(this.currentServerTime, 'seconds'), 0)
    } else {
      return 0
    }
  }

  get canCountDown() {
    return !!(
      !this.isAuctionFinished &&
      !this.hasAuctionEnded &&
      this.currentServerTime &&
      ((this.isAuctionInProgress && this.biddingEndTime) ||
        (!this.isAuctionInProgress && this.biddingStartTime))
    )
  }

  get useActiveAuctionPolling() {
    /**
     * POLLING_TIMER_FINISHING_THRESHOLD is so that we don't go into slow mode (NOT_IN_AUCTION)
     * if we're nearing countdown end.  This is specifically for when we transition from
     * bidding about to start to bidding started - we want more frequency so it doesn't sit there
     * in "in a moment" mode for a long time.
     */
    const withinTimerFinishingThreshold =
      this.remainingSeconds < POLLING_TIMER_FINISHING_THRESHOLD

    /**
     * Either:
     *  - we are in an active auction
     *  - or, we are close enough to starting that we want to trigger shorter polling periods
     *    to keep information up-to-date
     */
    return Boolean(
      this.isAuctionInProgress ||
        this.didAuctionEndRecently ||
        (this.canCountDown && withinTimerFinishingThreshold)
    )
  }

  getPollingInterval(finalOffer) {
    if (this.useActiveAuctionPolling) {
      return this._user.logged_in
        ? POLLING_INTERVAL_IN_AUCTION_LOGGED_IN
        : POLLING_INTERVAL_IN_AUCTION_NOT_LOGGED_IN
    } else if (this.hasUserWonAuction && !this.openContractUrl) {
      return POLLING_INTERVAL_PREPARING_CONTRACT_FORM
    } else if (isFinalOfferPending(this, finalOffer)) {
      return POLLING_INTERVAL_FINAL_OFFER_PENDING
    } else {
      return POLLING_INTERVAL_NOT_IN_AUCTION
    }
  }

  get hasAuctionEnded() {
    const currentDatetime = this.currentServerTime
    const bidEndDatetime = this.biddingEndTime

    return bidEndDatetime && currentDatetime
      ? moment(bidEndDatetime).isBefore(currentDatetime)
      : false
  }

  get didAuctionEndRecently() {
    const { biddingEndTime, currentServerTime } = this
    return (
      this.hasAuctionEnded &&
      moment(currentServerTime).diff(biddingEndTime, 'seconds') <
        POLLING_TIMER_AFTER_AUCTION_THRESHOLD
    )
  }

  get propertyStatusLabel() {
    return this._property_details.status_label
  }

  get statusLabel() {
    if (this.isREO) {
      return this.propertyStatusLabel === FOR_SALE && this.isAuctionGoneOrSold
        ? PENDING // force display label as Pending (IF it is For Sale) once auction sold/gone
        : this.propertyStatusLabel
    } else {
      let statusLabel = this._property_details.status_label
      if (statusLabel === ACTIVE_POSTPONED_TO) {
        let postponedDate = moment(this.postponedDate, 'YYYY-MM-DD')
        postponedDate = postponedDate.isValid()
          ? postponedDate.format('MMM DD')
          : 'TBD'
        statusLabel = `${statusLabel} ${postponedDate}`
      }
      return statusLabel
    }
  }

  get openContractUrl() {
    // There are checks on server side for the existence of a contract url.
    // This means there will not be a window to hook into for URL api.
    // https://git.ten-x.io/projects/RA/repos/resi-fe-pdp/browse/src/server/controllers/contract.js#27
    //
    // TODO: This is poor design. This external dep should not care about the details of what individual apps do. Especially
    // not in a model class, which is meant to operate on just data it's required to model.  We should remove this
    // restriction.
    if (!canUseDom()) {
      return (
        this._status.open_contract_url ||
        this._property.transaction_details.open_contract_url
      )
    }

    if (
      !this._status.open_contract_url &&
      !this._property.transaction_details.open_contract_url
    ) {
      return null
    }

    // NOTE: This needs to be a relative URL since we don't want the backend to care about the subdomain of the env.
    return new URL(
      this._status.open_contract_url ||
        this._property.transaction_details.open_contract_url,
      location.href
    ).pathname
  }

  get isWinnerContractCompleted() {
    return !this.openContractUrl && this.propertyStatusLabel === 'Pending'
  }

  get isSoldPending() {
    return (
      this.isAuctionSold && [PENDING, UNDER_CONTRACT].includes(this.statusLabel)
    )
  }

  get canAcceptOffers() {
    return this.isAuctionInPreSale && !this.isSoldPending
  }

  get venueDetails() {
    return _.get(this._property, 'transaction_details.venue', '')
  }

  get venueName() {
    return !this.isREO && _.get(this._property, 'event_details.venue_name', '')
  }

  get venueAddress() {
    return (
      !this.isREO && _.get(this._property, 'event_details.venue_address', '')
    )
  }

  get venueUrl() {
    return url.format(this._property.event_details.venue_url)
  }

  get venueGoogleMapsUrl() {
    return `https://www.google.com/maps/search/?api=1&query=${
      this.venueAddress || this.venueName
    }`
  }

  get eventDetailsUrl() {
    return _.get(this._property, 'event_details.event_details_url', '')
  }

  get usingSellerLevelFees() {
    return this.isTPSOnline
      ? !!this._property_details.seller_level_emd_and_bp_flag
      : !!this._property.transaction_details.seller_level_emd_and_bp_flag
  }

  get salesDeposit() {
    return this._property_details.sale_deposit_amt
  }

  get isTPSLive() {
    return !this.isREO && !this._property.is_tps_online
  }

  get isTPSOhio() {
    return this.isTPSOnline && this.propertyState === 'Ohio'
  }

  get isTPSOnline() {
    return !this.isREO && !!this._property.is_tps_online
  }

  get isFCLMarketing() {
    return (
      _.get(this._property_details, 'product_type_original') === 'FCL Marketing'
    )
  }

  /**
   * Gets the difference between start and end
   * @param  {moment} start Start datetime
   * @param  {moment} end   End datetime
   * @return {number}       Seconds representing end - start
   */
  getDuration(start, end) {
    return moment(end).diff(moment(start), 'seconds')
  }

  /**
   * @return {String} registration url for this property
   */
  get registrationUrl() {
    return this.isOnlineEvent
      ? `/registration/event/residential/${this.auctionNumber}/${this.eventPropertyId}`
      : 'http://events.auction.com/live/philadelphia-673/'
  }

  /**
   * @return {String} broker co-op registration url for this property
   */
  get brokerRegistrationUrl() {
    return `/registration/broker/event/residential/${this.auctionNumber}/${this.eventPropertyId}`
  }

  get hideStartingBid() {
    return HIDE_STARTING_BID_STATES.includes(this.propertyState)
  }

  @Memoize
  toTrackingContext() {
    return {
      reserveMetFlag: this.hasReserveBeenMet,
      proxybidflag: this.isProxyBiddingAllowed,
      'bid.increment': this.bidIncrement,
    }
  }
}
